import type { FailReason } from 'utils/failReasonLabels';

import type { BaseModel } from './baseModel';

interface CrmUploadHistoryEntry {
  requestPayload: unknown;
  response: unknown;
  datetime: string;
}

export interface Lead extends BaseModel {
  businessUnitId?: number;
  clientId: number;
  source: string;
  origin: string;
  subOrigin: string | null;
  initialBrandOfInterest: string | null;
  initialModelOfInterest: string | null;
  modelsOfInterest?: Array<{
    brand: string;
    model: string;
    version?: string;
    isNew: boolean;
    carModelId?: number;
  }> | null;
  initialMessage: string | null;
  webhookBodyAtCreation: string | null;
  outboundCampaignId: string | null;
  CRMUpdateAt: Date | null;
  CRMCreationPayload: object | null;
  CRMCreationResponse: object | null;
  crmUploadReason?: 'interested' | null;
  messagesCount: number;
  clientMessagesCount: number;
  discarded: boolean;
  status: string;
  url: string | null;
  UTM?: {
    source: string;
    medium: string;
    campaign: string;
    content: string;
    term: string;
  };
  visitDate?: Date | null;
  endReason?: string | null;
  topics?: string[] | null;
  AIComment?: string | null;
  comment?: string | null;
  externalId?: string | null;
  crmUploadHistory: CrmUploadHistoryEntry[] | null;
  metaAdId?: number | null;
  purchased?: boolean;
  statusHistory?: Array<{
    status: string;
    event?: string;
    triggeredBy?: string;
    triggeredAt?: string;
    stateData?: {
      assignedUserId?: number;
      closedState?: string | null;
    };
  }> | null;
  businessUnitAd?: {
    adSourceId: string;
    adSource: string;
    campaignSourceName: string | null;
    campaignExternalCRMId?: string | null;
    productOfInterest?: string | null;
  };
  metaAd?: {
    name: string | null;
    metaAdId: string | null;
    metaAdSet?: {
      name: string | null;
      metaCampaign?: {
        name: string | null;
      };
    };
  };
  dealership: string | null;
  branchOffice: string | null;
  externalLeadOwner: string | null;
  failedMessagesCount: number;
  failReason?: FailReason | null;
  templatesSentCount: number;
  customAttributes?: {
    insurexData?: {
      loanProtectionInsurance?: {
        advancePercentage?: number;
      };
      unemploymentInsurance?: {
        advancePercentage?: number;
      };
    };
    handoff?: {
      fromBusinessUnitId: number;
      toBusinessUnitId: number;
      reason: string;
      handedOffAt: string;
    };
  };
  isCrmDuplicate?: boolean;
  isTestLead?: boolean;
  whatsAppCampaign?: {
    id: number;
    name: string;
  };
  utmFromWebhook?: {
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
    utmId?: string;
    utmTerm?: string;
    utmContent?: string;
    utmUrlOrigin?: string;
  };
  sales?: Array<{
    id: number;
    insuranceType: 'loanProtectionInsurance' | 'unemploymentInsurance';
    advancePercentage: number | null;
  }>;
}

export interface ExtendedLead extends Lead {
  client?: {
    id: number;
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    rut: string | null;
    messagesCount: number;
    clientMessagesCount: number;
    failedMessagesCount: number;
    userId: number;
    businessUnitId: number;
    disabledAutomaticResponses?: boolean | null;
    disabledFollowUps?: boolean | null;
    lastMessageDate: Date | null;
    lastSentMessageRole?: 'user' | 'assistant' | 'system' | null;
    creditStatus?: string | null;
    creditSimulations?: unknown[] | null;
    notes?: string | null;
    customAttributes?: {
      income?: number;
      debtsMonthlyPayment?: number;
      financialLoadRange?: 'low' | 'medium' | 'high';
      isInDicom?: boolean;
      askedForFinancing?: boolean;
      insurexData?: {
        loanProtectionInsurance?: {
          advancePercentage?: number;
        };
        unemploymentInsurance?: {
          advancePercentage?: number;
        };
      };
    };
    user?: {
      id: number;
      name: string;
    };
    businessUnit: {
      id: number;
      name: string;
      type: 'car_dealership' | 'financial_institution' | 'insurance_broker';
    };
  };
}

export const ALLOWED_CLOSE_LEAD_STATUSES = ['Éxito', 'Fracaso'] as const;
